import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API, getAuthHeader } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Plus, Edit, Trash2, Upload, Download, CheckCircle, XCircle, FileSpreadsheet, History, Clock, MoreVertical, Battery, Eye, FileText } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { useTranslation } from 'react-i18next';
import { formatDateDisplay, formatDateTimeDisplay } from '../utils/dateUtils';
import useDeviceDetection from '../hooks/useDeviceDetection';
import { formatErrorMessage } from '../utils/formatError';

const Batteries = () => {
  const { t } = useTranslation();
  const { isMobile, isTablet } = useDeviceDetection();
  const [batteries, setBatteries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState([]);
  const [importBatteries, setImportBatteries] = useState([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importMode, setImportMode] = useState('skip'); // 'skip', 'update', 'fail'
  const [editingId, setEditingId] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedBattery, setSelectedBattery] = useState(null);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [batteryHistory, setBatteryHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [filteredBatteries, setFilteredBatteries] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    battery_id: '',
    serial_number: '',
    capacity_ah: 0,
    status: 'available',
    health_percentage: 100
  });

  useEffect(() => {
    fetchBatteries();
  }, []);

  // Re-fetch from server when search/status changes (debounced).
  // Skip the very first run because the mount effect above already fetched.
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    const timer = setTimeout(() => {
      fetchBatteries();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, statusFilter]);

  useEffect(() => {
    // Server already filters; just mirror to filteredBatteries for the table
    setFilteredBatteries(batteries);
  }, [batteries]);

  const handleViewDetails = (battery) => {
    setSelectedBattery(battery);
    setDetailDialogOpen(true);
  };

  const fetchBatteryHistory = async (batterySerial) => {
    try {
      setLoadingHistory(true);
      const response = await axios.get(`${API}/battery-history/${batterySerial}`, {
        headers: getAuthHeader()
      });
      setBatteryHistory(response.data || []);
    } catch (error) {
      console.error('Error fetching battery history:', error);
      toast.error('Failed to fetch battery history');
      setBatteryHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleViewHistory = async (battery) => {
    setSelectedBattery(battery);
    await fetchBatteryHistory(battery.serial_number);
    setHistoryDialogOpen(true);
  };

  const fetchBatteries = async () => {
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      const response = await axios.get(`${API}/batteries?${params.toString()}`, {
        headers: getAuthHeader()
      });
      setBatteries(response.data);
    } catch (error) {
      toast.error('Failed to fetch batteries');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API}/batteries/${editingId}`, formData, {
          headers: getAuthHeader()
        });
        toast.success('Battery updated successfully');
      } else {
        await axios.post(`${API}/batteries`, formData, {
          headers: getAuthHeader()
        });
        toast.success('Battery added successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchBatteries();
    } catch (error) {
      toast.error(formatErrorMessage(error, 'Operation failed'));
    }
  };

  const handleEdit = (battery) => {
    setEditingId(battery.id);
    setFormData({
      battery_id: battery.battery_id,
      serial_number: battery.serial_number,
      capacity_ah: battery.capacity_ah,
      status: battery.status,
      health_percentage: battery.health_percentage
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure?')) {
      try {
        await axios.delete(`${API}/batteries/${id}`, {
          headers: getAuthHeader()
        });
        toast.success('Battery deleted successfully');
        fetchBatteries();
      } catch (error) {
        toast.error('Failed to delete battery');
      }
    }
  };

  const resetForm = () => {
    setFormData({ battery_id: '', serial_number: '', capacity_ah: 0, status: 'available', health_percentage: 100 });
    setEditingId(null);
  };

  // ----- Exports (client-side, uses the currently-filtered inventory) -----
  const buildExportRows = () => filteredBatteries.map(b => ({
    'Battery ID': b.battery_id || '',
    'Serial Number': b.serial_number || '',
    'Capacity (Ah)': b.capacity_ah ?? '',
    'Voltage': b.voltage ?? '',
    'Status': b.status || '',
    'Health %': b.health_percentage ?? '',
    'Swap Count': b.swap_count ?? 0,
    'Purchase Date': b.purchase_date ? formatDateDisplay(b.purchase_date) : '',
    'Current Bike': b.current_bike_id || b.current_motorcycle_id || '',
    'Notes': b.notes || '',
  }));

  const handleExportExcel = () => {
    const rows = buildExportRows();
    if (rows.length === 0) {
      toast.error('No batteries to export');
      return;
    }
    const ws = XLSX.utils.json_to_sheet(rows);
    ws['!cols'] = [
      { wch: 12 }, { wch: 16 }, { wch: 13 }, { wch: 10 }, { wch: 12 },
      { wch: 10 }, { wch: 12 }, { wch: 14 }, { wch: 14 }, { wch: 26 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Batteries');
    const filterLabel = statusFilter && statusFilter !== 'all' ? `_${statusFilter}` : '';
    XLSX.writeFile(wb, `battery_inventory${filterLabel}_${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success(`Exported ${rows.length} batteries to Excel`);
  };

  const handleExportPDF = async () => {
    const rows = buildExportRows();
    if (rows.length === 0) {
      toast.error('No batteries to export');
      return;
    }
    // Lazy-load pdf libs so they don't inflate the initial bundle.
    const { default: jsPDF } = await import('jspdf');
    const autoTableMod = await import('jspdf-autotable');
    const autoTable = autoTableMod.default || autoTableMod.autoTable;
    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    doc.setFontSize(14);
    doc.text('Battery Inventory', 40, 40);
    doc.setFontSize(9);
    const filterLabel = statusFilter && statusFilter !== 'all' ? ` — Filter: ${statusFilter}` : '';
    doc.text(`Generated ${new Date().toLocaleString()} • ${rows.length} batter${rows.length === 1 ? 'y' : 'ies'}${filterLabel}`, 40, 56);
    autoTable(doc, {
      startY: 72,
      head: [Object.keys(rows[0])],
      body: rows.map(r => Object.values(r)),
      styles: { fontSize: 7.5, cellPadding: 4, overflow: 'linebreak' },
      headStyles: { fillColor: [101, 163, 13], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [245, 250, 240] },
      margin: { left: 30, right: 30 },
    });
    doc.save(`battery_inventory${filterLabel ? '_' + statusFilter : ''}_${new Date().toISOString().slice(0, 10)}.pdf`);
    toast.success(`Exported ${rows.length} batteries to PDF`);
  };

  const downloadTemplate = () => {
    const template = [
      {
        'Serial Number': 'EMBY2510001',
        'Battery ID': 'BAT0001',
        'Capacity (Ah)': 35,
        'Voltage': 48,
        'Status': 'available',
        'Health %': 100,
        'Charge Cycles': 0,
        'Purchase Date': '2024-01-01'
      },
      {
        'Serial Number': 'EM24010001',
        'Battery ID': 'BAT0002',
        'Capacity (Ah)': 40,
        'Voltage': 52,
        'Status': 'available',
        'Health %': 95,
        'Charge Cycles': 50,
        'Purchase Date': '2024-01-15'
      }
    ];

    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Batteries');
    XLSX.writeFile(wb, 'battery_import_template.xlsx');
    toast.success('Template downloaded!');
  };

  const handleFileSelect = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    if (!selectedFile.name.endsWith('.xlsx') && !selectedFile.name.endsWith('.xls')) {
      toast.error('Please select an Excel file (.xlsx or .xls)');
      return;
    }

    setImportFile(selectedFile);
    parseExcelFile(selectedFile);
  };

  const parseExcelFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        if (jsonData.length === 0) {
          toast.error('Excel file is empty');
          return;
        }

        const mappedBatteries = jsonData.map((row, index) => ({
          serial_number: row['Serial Number'] || row['serial_number'] || '',
          battery_id: row['Battery ID'] || row['battery_id'] || `BAT${String(index + 1).padStart(4, '0')}`,
          capacity_ah: parseFloat(row['Capacity (Ah)'] || row['capacity_ah'] || 35),
          voltage: parseFloat(row['Voltage'] || row['voltage'] || 48),
          status: row['Status'] || row['status'] || 'available',
          health_percentage: parseFloat(row['Health %'] || row['health_percentage'] || 100),
          charge_cycles: parseInt(row['Charge Cycles'] || row['charge_cycles'] || 0),
          purchase_date: row['Purchase Date'] || row['purchase_date'] || '2024-01-01'
        }));

        const validBatteries = mappedBatteries.filter(b => b.serial_number);
        
        if (validBatteries.length === 0) {
          toast.error('No valid batteries found. Serial Number column is required.');
          return;
        }

        setImportBatteries(validBatteries);
        setImportPreview(validBatteries.slice(0, 10));
        toast.success(`✅ ${validBatteries.length} batteries loaded from Excel`);
      } catch (error) {
        toast.error('Failed to parse Excel file');
        console.error(error);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleImport = async () => {
    if (importBatteries.length === 0) {
      toast.error('No batteries to import');
      return;
    }

    setImporting(true);
    try {
      const response = await axios.post(
        `${API}/batteries/import-excel`,
        { batteries: importBatteries, mode: importMode },
        { headers: getAuthHeader() }
      );

      setImportResult(response.data);
      
      // Show appropriate messages based on results
      if (response.data.success_count > 0) {
        toast.success(`✅ Imported ${response.data.success_count} new batteries!`);
      }
      
      if (response.data.updated_count > 0) {
        toast.success(`🔄 Updated ${response.data.updated_count} existing batteries!`);
      }
      
      if (response.data.skipped_count > 0) {
        toast.info(`⏭️ Skipped ${response.data.skipped_count} existing batteries`);
      }
      
      if (response.data.failed_count > 0) {
        toast.warning(`⚠️ ${response.data.failed_count} batteries failed to import`);
      }

      // Close dialog if everything was processed (no failures)
      if (response.data.failed_count === 0) {
        setImportFile(null);
        setImportBatteries([]);
        setImportPreview([]);
        setImportDialogOpen(false);
        setImportResult(null);
        setImportMode('skip');
      }

      fetchBatteries();
    } catch (error) {
      toast.error('Failed to import batteries');
      console.error(error);
    } finally {
      setImporting(false);
    }
  };

  const getStatusColor = (status) => {
    const normalizedStatus = (status || '').toLowerCase().replace(/[_\s]/g, '-');
    const colors = {
      'available': 'bg-green-100 text-green-800',
      'in-use': 'bg-blue-100 text-blue-800',
      'charging': 'bg-yellow-100 text-yellow-800',
      'faulty': 'bg-red-100 text-red-800',
      'maintenance': 'bg-orange-100 text-orange-800'
    };
    return colors[normalizedStatus] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96">Loading...</div>;
  }

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="batteries-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-800">{t('batteries.title')}</h1>
          <p className="text-sm sm:text-base text-slate-600 mt-1">{t('batteries.subtitle')}</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-lime-600 to-green-600" size={isMobile ? "sm" : "default"} data-testid="add-battery-btn">
              <Plus className="w-4 h-4 mr-2" />
              {isMobile ? 'Add' : t('batteries.addBattery')}
            </Button>
          </DialogTrigger>
          <DialogContent className={`${isMobile ? 'w-[95vw] max-w-[95vw]' : 'max-w-lg'} max-h-[90vh] overflow-y-auto`}>
            <DialogHeader>
              <DialogTitle>{editingId ? t('batteries.editBattery') : t('batteries.addBattery')}</DialogTitle>
              <DialogDescription>{t('batteries.subtitle')}</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className={`grid gap-4 ${isMobile ? 'grid-cols-1' : 'grid-cols-2'}`}>
                <div className="space-y-2">
                  <Label>Battery ID</Label>
                  <Input
                    value={formData.battery_id}
                    onChange={(e) => setFormData({ ...formData, battery_id: e.target.value })}
                    placeholder="BAT-1001"
                    required
                    data-testid="battery-id"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Serial Number (11 digits)</Label>
                  <Input
                    value={formData.serial_number}
                    onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
                    placeholder="ABC12345678"
                    maxLength={11}
                    required
                    data-testid="battery-serial"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Capacity (Ah)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.capacity_ah}
                    onChange={(e) => setFormData({ ...formData, capacity_ah: parseFloat(e.target.value) })}
                    placeholder="e.g., 48.5"
                    required
                    data-testid="battery-capacity"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Health Percentage</Label>
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={formData.health_percentage}
                    onChange={(e) => setFormData({ ...formData, health_percentage: parseFloat(e.target.value) })}
                    required
                    data-testid="battery-health"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                    <SelectTrigger data-testid="battery-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="available">{t('batteries.statusAvailable')}</SelectItem>
                      <SelectItem value="in-use">{t('batteries.statusInUse')}</SelectItem>
                      <SelectItem value="charging">{t('batteries.statusCharging')}</SelectItem>
                      <SelectItem value="faulty">{t('batteries.statusFaulty')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-gradient-to-r from-lime-600 to-green-600" data-testid="submit-battery-btn">
                  {editingId ? 'Update' : 'Add'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Battery Import Dialog */}
      <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <DialogContent className={`${isMobile ? 'w-[95vw] max-w-[95vw]' : 'max-w-4xl'} max-h-[90vh] overflow-y-auto`}>
          <DialogHeader>
            <DialogTitle>Import Batteries from Excel</DialogTitle>
            <DialogDescription>
              Upload an Excel file to import multiple batteries at once
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <Alert>
              <FileSpreadsheet className="h-4 w-4" />
              <AlertDescription>
                <div className="space-y-2">
                  <div><strong>Required Column:</strong> Serial Number</div>
                  <div><strong>Optional Columns:</strong> Battery ID, Capacity (Ah), Voltage, Status, Health %, Charge Cycles, Purchase Date</div>
                  <div className="text-sm text-slate-600">Download the template to see the correct format.</div>
                </div>
              </AlertDescription>
            </Alert>

            <div className="flex items-center gap-4">
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
                className="hidden"
                id="battery-excel-upload-batteries-page"
              />
              <label
                htmlFor="battery-excel-upload-batteries-page"
                className="flex items-center justify-center px-6 py-4 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-lime-500 hover:bg-lime-50 transition-colors w-full"
              >
                <div className="text-center">
                  <Upload className="w-12 h-12 mx-auto text-slate-400 mb-2" />
                  <div className="text-lg font-semibold text-slate-700">
                    {importFile ? importFile.name : 'Click to upload Excel file'}
                  </div>
                  <div className="text-sm text-slate-500 mt-1">
                    Supports .xlsx and .xls files
                  </div>
                </div>
              </label>
            </div>

            {importBatteries.length > 0 && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-green-800 font-semibold">
                  <CheckCircle className="w-5 h-5" />
                  {importBatteries.length} batteries ready to import
                </div>
              </div>
            )}

            {importPreview.length > 0 && (
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-slate-100 px-4 py-2 font-semibold">Preview (First 10 Batteries)</div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="border p-2 text-left text-xs">#</th>
                        <th className="border p-2 text-left text-xs">Serial Number</th>
                        <th className="border p-2 text-left text-xs">Battery ID</th>
                        <th className="border p-2 text-left text-xs">Capacity</th>
                        <th className="border p-2 text-left text-xs">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importPreview.map((battery, index) => (
                        <tr key={index} className="hover:bg-slate-50">
                          <td className="border p-2 text-xs">{index + 1}</td>
                          <td className="border p-2 text-xs font-mono">{battery.serial_number}</td>
                          <td className="border p-2 text-xs">{battery.battery_id}</td>
                          <td className="border p-2 text-xs">{battery.capacity_ah} Ah</td>
                          <td className="border p-2 text-xs">
                            <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs">
                              {battery.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {importBatteries.length > 10 && (
                  <div className="px-4 py-2 text-sm text-slate-600 bg-slate-50">
                    ... and {importBatteries.length - 10} more batteries
                  </div>
                )}
              </div>
            )}

            {importBatteries.length > 0 && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">
                  How to handle existing batteries?
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="importMode"
                      value="skip"
                      checked={importMode === 'skip'}
                      onChange={(e) => setImportMode(e.target.value)}
                      className="text-lime-600"
                    />
                    <span className="text-sm">Skip existing (add new only)</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="importMode"
                      value="update"
                      checked={importMode === 'update'}
                      onChange={(e) => setImportMode(e.target.value)}
                      className="text-lime-600"
                    />
                    <span className="text-sm">Update existing batteries</span>
                  </label>
                </div>
              </div>
            )}

            {importResult && (
              <div className="space-y-2">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <div className="flex items-center gap-2 text-green-800">
                      <CheckCircle className="w-5 h-5" />
                      <div>
                        <div className="font-semibold text-xs">New</div>
                        <div className="text-xl font-bold">{importResult.success_count}</div>
                      </div>
                    </div>
                  </div>
                  
                  {importResult.updated_count > 0 && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                      <div className="flex items-center gap-2 text-blue-800">
                        <CheckCircle className="w-5 h-5" />
                        <div>
                          <div className="font-semibold text-xs">Updated</div>
                          <div className="text-xl font-bold">{importResult.updated_count}</div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {importResult.skipped_count > 0 && (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                      <div className="flex items-center gap-2 text-yellow-800">
                        <Clock className="w-5 h-5" />
                        <div>
                          <div className="font-semibold text-xs">Skipped</div>
                          <div className="text-xl font-bold">{importResult.skipped_count}</div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <div className="flex items-center gap-2 text-red-800">
                      <XCircle className="w-5 h-5" />
                      <div>
                        <div className="font-semibold text-xs">Failed</div>
                        <div className="text-xl font-bold">{importResult.failed_count}</div>
                      </div>
                    </div>
                  </div>
                </div>

                {importResult.errors && importResult.errors.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 max-h-40 overflow-y-auto">
                    <div className="font-semibold text-red-800 text-sm mb-1">Errors:</div>
                    <ul className="list-disc list-inside text-xs text-red-700 space-y-1">
                      {importResult.errors.slice(0, 10).map((error, index) => (
                        <li key={index}>{error}</li>
                      ))}
                      {importResult.errors.length > 10 && (
                        <li>... and {importResult.errors.length - 10} more errors</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setImportDialogOpen(false)}>
                Cancel
              </Button>
              {importBatteries.length > 0 && (
                <Button 
                  onClick={handleImport} 
                  disabled={importing}
                  className="bg-gradient-to-r from-lime-600 to-green-600"
                >
                  {importing ? 'Importing...' : 'Import ' + importBatteries.length + ' Batteries'}
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Card className="border-0 shadow-lg">
        <CardHeader>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
            <CardTitle>Battery Inventory ({filteredBatteries.length})</CardTitle>
            <div className="flex gap-2 flex-wrap items-center">
              <Input
                type="text"
                placeholder="Search by serial..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={`${isMobile ? 'w-36' : 'w-56'}`}
                data-testid="battery-search-input"
              />
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className={`${isMobile ? 'w-32' : 'w-48'}`}>
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Batteries</SelectItem>
                  <SelectItem value="available">{t('batteries.statusAvailable')}</SelectItem>
                  <SelectItem value="in-use">{t('batteries.statusInUse')}</SelectItem>
                  <SelectItem value="charging">{t('batteries.statusCharging')}</SelectItem>
                  <SelectItem value="faulty">{t('batteries.statusFaulty')}</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportExcel}
                className="gap-2"
                data-testid="battery-export-excel-btn"
              >
                <FileSpreadsheet className="w-4 h-4" />
                {!isMobile && 'Export Excel'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportPDF}
                className="gap-2"
                data-testid="battery-export-pdf-btn"
              >
                <FileText className="w-4 h-4" />
                {!isMobile && 'Export PDF'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={downloadTemplate}
                className="gap-2"
              >
                <Download className="w-4 h-4" />
                {!isMobile && 'Download Template'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setImportDialogOpen(true)}
                className="gap-2"
              >
                <Upload className="w-4 h-4" />
                {!isMobile && 'Import from Excel'}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Mobile Card View */}
          {(isMobile || isTablet) ? (
            <div className="space-y-3">
              {filteredBatteries.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                  <Battery className="w-12 h-12 mb-3 text-slate-300" />
                  <p className="text-sm">No batteries found</p>
                </div>
              ) : (
                filteredBatteries.map((battery) => (
                  <Card 
                    key={battery.id} 
                    className="overflow-hidden cursor-pointer active:scale-[0.99] transition-transform"
                    onClick={() => handleViewDetails(battery)}
                    data-testid={`battery-card-${battery.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2 flex-wrap">
                            <span className="font-bold text-slate-800">{battery.battery_id}</span>
                            <Badge className={getStatusColor(battery.status)}>
                              {battery.status}
                            </Badge>
                          </div>
                          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
                            <div>
                              <span className="text-slate-500 text-xs">Serial:</span>
                              <p className="text-slate-700 truncate">{battery.serial_number}</p>
                            </div>
                            <div>
                              <span className="text-slate-500 text-xs">Capacity:</span>
                              <p className="text-slate-700">{battery.capacity_ah} Ah</p>
                            </div>
                            <div>
                              <span className="text-slate-500 text-xs">Health:</span>
                              <p className="text-slate-700">{battery.health_percentage}%</p>
                            </div>
                            <div>
                              <span className="text-slate-500 text-xs">Swaps:</span>
                              <p className="text-slate-700">{battery.swap_count || 0}</p>
                            </div>
                          </div>
                        </div>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleViewHistory(battery); }}>
                              <History className="w-4 h-4 mr-2" />
                              View History
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleEdit(battery); }}>
                              <Edit className="w-4 h-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleDelete(battery.id); }} className="text-red-600">
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          ) : (
            /* Desktop Table View */
            <div className="rounded-lg border border-slate-200">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Battery ID</TableHead>
                    <TableHead>Serial Number</TableHead>
                    <TableHead>Capacity (Ah)</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>History</TableHead>
                    <TableHead>Swap Count</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {batteries.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-slate-500 py-8">
                        No batteries found. Add your first battery to get started.
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredBatteries.map((battery) => (
                      <TableRow 
                        key={battery.id} 
                        data-testid={`battery-row-${battery.id}`}
                        className="cursor-pointer hover:bg-gray-50"
                        onClick={() => handleViewDetails(battery)}
                      >
                        <TableCell className="font-medium">{battery.battery_id}</TableCell>
                        <TableCell className="text-xs">{battery.serial_number}</TableCell>
                        <TableCell>{battery.capacity_ah} Ah</TableCell>
                        <TableCell>
                          <Badge className={getStatusColor(battery.status)}>
                            {battery.status}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewHistory(battery);
                            }}
                            className="gap-1"
                          >
                            <History className="w-3 h-3" />
                            View History
                          </Button>
                        </TableCell>
                        <TableCell>{battery.swap_count}</TableCell>
                        <TableCell>
                          <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                            <Button size="sm" variant="outline" onClick={() => handleEdit(battery)} data-testid={`edit-battery-${battery.id}`}>
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => handleDelete(battery.id)} data-testid={`delete-battery-${battery.id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Battery Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className={`${isMobile ? 'w-[95vw] max-w-[95vw]' : 'max-w-2xl'} max-h-[90vh] overflow-y-auto`}>
          <DialogHeader>
            <DialogTitle>Battery Details - {selectedBattery?.battery_id}</DialogTitle>
            <DialogDescription>Complete battery information</DialogDescription>
          </DialogHeader>
          {selectedBattery && (
            <div className="space-y-4">
              <Card>
                <CardHeader><CardTitle className="text-lg">Battery Information</CardTitle></CardHeader>
                <CardContent>
                  <div className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-2'} gap-4`}>
                    <div>
                      <Label className="text-slate-600">Battery ID</Label>
                      <p className="font-medium">{selectedBattery.battery_id}</p>
                    </div>
                    <div>
                      <Label className="text-slate-600">Serial Number</Label>
                      <p className="font-medium">{selectedBattery.serial_number}</p>
                    </div>
                    <div>
                      <Label className="text-slate-600">Capacity</Label>
                      <p className="font-medium">{selectedBattery.capacity_ah} Ah</p>
                    </div>
                    <div>
                      <Label className="text-slate-600">Health</Label>
                      <p className="font-medium">{selectedBattery.health_percentage}%</p>
                    </div>
                    <div>
                      <Label className="text-slate-600">Status</Label>
                      <Badge className={getStatusColor(selectedBattery.status)}>
                        {selectedBattery.status}
                      </Badge>
                    </div>
                    <div>
                      <Label className="text-slate-600">Total Swaps</Label>
                      <p className="font-medium">{selectedBattery.swap_count || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
          <div className="flex justify-end mt-4">
            <Button onClick={() => setDetailDialogOpen(false)}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Battery History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className={`${isMobile ? 'w-[95vw] max-w-[95vw]' : 'max-w-5xl'} h-[90vh] flex flex-col`}>
          <DialogHeader className="flex-shrink-0 pb-4">
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-600" />
              Battery History - {selectedBattery?.serial_number}
            </DialogTitle>
            <DialogDescription>
              Complete swap history and tracking details for this battery
            </DialogDescription>
          </DialogHeader>

          {loadingHistory ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-3"></div>
                <p>Loading battery history...</p>
              </div>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto pr-2">
              <div className="space-y-4">
                {/* Summary Cards */}
                {selectedBattery && (
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                    <Card className="border-blue-200 bg-blue-50">
                      <CardContent className="p-4 text-center">
                        <div className="text-2xl font-bold text-blue-700">{batteryHistory.length}</div>
                        <div className="text-sm text-blue-600">Total Swaps</div>
                      </CardContent>
                    </Card>
                    <Card className="border-green-200 bg-green-50">
                      <CardContent className="p-4 text-center">
                        <div className="text-2xl font-bold text-green-700">{selectedBattery.serial_number}</div>
                        <div className="text-sm text-green-600">Serial Number</div>
                      </CardContent>
                    </Card>
                    <Card className="border-purple-200 bg-purple-50">
                      <CardContent className="p-4 text-center">
                        <div className="text-2xl font-bold text-purple-700">{selectedBattery.status}</div>
                        <div className="text-sm text-purple-600">Current Status</div>
                      </CardContent>
                    </Card>
                    <Card className="border-orange-200 bg-orange-50">
                      <CardContent className="p-4 text-center">
                        <div className="text-2xl font-bold text-orange-700">{selectedBattery.capacity_ah} Ah</div>
                        <div className="text-sm text-orange-600">Capacity</div>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* History Table */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Swap History Timeline</CardTitle>
                    <DialogDescription>
                      Chronological record of all battery swaps for this battery
                    </DialogDescription>
                  </CardHeader>
                  <CardContent>
                    {batteryHistory.length === 0 ? (
                      <div className="text-center py-8 text-slate-500">
                        <History className="w-12 h-12 mx-auto mb-3 opacity-30" />
                        <p>No swap history found for this battery</p>
                        <p className="text-sm">This battery hasn't been used in any swaps yet</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>#</TableHead>
                              <TableHead>Date & Time</TableHead>
                              <TableHead>Rider Name</TableHead>
                              <TableHead>Rider ID</TableHead>
                              <TableHead>Bike Number</TableHead>
                              <TableHead>Lessee Company</TableHead>
                              <TableHead>Battery Type</TableHead>
                              <TableHead>SOC %</TableHead>
                              <TableHead>ODO Reading</TableHead>
                              <TableHead>Location</TableHead>
                              <TableHead>Technician</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {batteryHistory.map((swap, index) => (
                              <TableRow key={swap.id || index} className="hover:bg-gray-50">
                                <TableCell className="font-medium">#{batteryHistory.length - index}</TableCell>
                                <TableCell className="text-sm">
                                  <div>{formatDateDisplay(swap.date || swap.swap_date)}</div>
                                  <div className="text-xs text-gray-500">{swap.time || 'N/A'}</div>
                                </TableCell>
                                <TableCell className="font-medium">{swap.rider_name || 'N/A'}</TableCell>
                                <TableCell className="text-xs font-mono">
                                  {swap.rider_unique_id && (
                                    <Badge variant="outline" className="bg-blue-50 text-blue-700">
                                      {swap.rider_unique_id}
                                    </Badge>
                                  )}
                                </TableCell>
                                <TableCell className="font-mono text-sm">{swap.bike_number || 'N/A'}</TableCell>
                                <TableCell className="text-sm">{swap.lessee_company || 'N/A'}</TableCell>
                                <TableCell>
                                  <Badge className={swap.battery_type === 'returned' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}>
                                    {swap.battery_type === 'returned' ? 'Returned' : 'Issued'}
                                  </Badge>
                                </TableCell>
                                <TableCell>
                                  <div className="flex items-center gap-2">
                                    <div className="text-sm font-medium">{swap.soc || 'N/A'}%</div>
                                    {swap.soc && (
                                      <div className="w-16 bg-gray-200 rounded-full h-2">
                                        <div 
                                          className={`h-2 rounded-full ${
                                            swap.soc > 80 ? 'bg-green-500' : 
                                            swap.soc > 50 ? 'bg-yellow-500' : 'bg-red-500'
                                          }`}
                                          style={{ width: `${Math.min(swap.soc, 100)}%` }}
                                        ></div>
                                      </div>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell className="text-sm">{swap.odo_reading || 'N/A'} km</TableCell>
                                <TableCell className="text-sm">{swap.location || 'N/A'}</TableCell>
                                <TableCell className="text-sm">{swap.technician_name || 'N/A'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          <div className="flex-shrink-0 flex justify-end pt-4 border-t">
            <Button onClick={() => setHistoryDialogOpen(false)}>Close</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Batteries;
